import { DatePicker } from "antd";
import dayjs from "dayjs";
import { Calendar, PlusCircle } from "feather-icons-react/build/IconComponents";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import TextEditor from "../../../feature-module/inventory/texteditor";
import { MinusCircle } from "react-feather";
import {
  getAllProductsForPurchase,
  getAllSuppliersForPurchase,
  getAllWarehousesForPurchase,
  getLocalizedValue,
  getProductDisplayName,
  getProductId,
  getProductSku,
  getSupplierDisplayName,
  updatePurchase,
} from "../../../api/purchaseApi";

const statusOptions = [
  { value: "Received", label: "Received" },
  { value: "Pending", label: "Pending" },
  { value: "Ordered", label: "Ordered" },
];

const objectIdOrObject = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.shape({
    _id: PropTypes.string,
  }),
]);

const productPropType = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.shape({
    _id: PropTypes.string,
    id: PropTypes.string,
    sku: PropTypes.string,
    SKU: PropTypes.string,
    productName: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    Product_Name: PropTypes.string,
  }),
]);

const normalizeId = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") {
    if (value?._id) return String(value._id);
    if (value?.id) return String(value.id);
    if (value?.value) return String(value.value);
    return "";
  }
  return String(value);
};

const toNumber = (value, fallback = 0) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const round2 = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

const buildEmptyForm = () => ({
  id: "",
  supplierId: "",
  warehouseId: "",
  purchaseDate: dayjs(),
  reference: "",
  orderTax: 0,
  discount: 0,
  shipping: 0,
  paid: 0,
  status: "Pending",
  note: "",
});

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
};

const EditPurchases = ({ purchaseData, onSuccess }) => {
  const lang = localStorage.getItem("lang") || "en";
  const t = (key) => (window.translate ? window.translate(key) : key);

  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);

  const [formData, setFormData] = useState(buildEmptyForm());
  const [selectedProductId, setSelectedProductId] = useState("");
  const [items, setItems] = useState([]);

  const commonSelectProps = {
    classNamePrefix: "react-select",
    isClearable: true,
    menuPortalTarget: document.body,
    menuPosition: "fixed",
    menuShouldScrollIntoView: false,
    blurInputOnSelect: true,
    closeMenuOnSelect: true,
    styles: selectStyles,
  };

  const loadDropdowns = async () => {
    try {
      setDropdownLoading(true);

      const [supplierRes, productRes, warehouseRes] = await Promise.all([
        getAllSuppliersForPurchase(),
        getAllProductsForPurchase(),
        getAllWarehousesForPurchase(),
      ]);

      const supplierList = Array.isArray(supplierRes?.suppliers)
        ? supplierRes.suppliers
        : Array.isArray(supplierRes)
        ? supplierRes
        : [];

      const productList = Array.isArray(productRes?.products)
        ? productRes.products
        : Array.isArray(productRes)
        ? productRes
        : [];

      const warehouseList = Array.isArray(warehouseRes?.warehouses)
        ? warehouseRes.warehouses
        : Array.isArray(warehouseRes)
        ? warehouseRes
        : [];

      setSuppliers(supplierList);
      setProducts(productList);
      setWarehouses(warehouseList);
    } catch (error) {
      console.error("Failed to load purchase dropdowns:", error);
    } finally {
      setDropdownLoading(false);
    }
  };

  useEffect(() => {
    loadDropdowns();
  }, []);

  const supplierOptions = useMemo(() => {
    return suppliers
      .map((supplier) => ({
        value: normalizeId(supplier?._id || supplier?.id),
        label: getSupplierDisplayName(supplier, lang) || t("Unnamed Supplier"),
        raw: supplier,
      }))
      .filter((item) => item.value);
  }, [suppliers, lang]);

  const productOptions = useMemo(() => {
    return products
      .map((product) => ({
        value: normalizeId(getProductId(product)),
        label: getProductDisplayName(product, lang) || t("Unnamed Product"),
        raw: product,
      }))
      .filter((item) => item.value);
  }, [products, lang]);

  const warehouseOptions = useMemo(() => {
    return warehouses
      .map((warehouse) => ({
        value: normalizeId(warehouse?._id || warehouse?.id),
        label: getLocalizedValue(warehouse?.warehouseName, lang) || t("Warehouse"),
        raw: warehouse,
      }))
      .filter((item) => item.value);
  }, [warehouses, lang]);

  const selectedSupplierOption = useMemo(() => {
    const currentId = normalizeId(formData.supplierId);
    return (
      supplierOptions.find(
        (option) => normalizeId(option.value) === currentId
      ) || null
    );
  }, [supplierOptions, formData.supplierId]);

  const selectedWarehouseOption = useMemo(() => {
    const currentId = normalizeId(formData.warehouseId);
    return (
      warehouseOptions.find(
        (option) => normalizeId(option.value) === currentId
      ) || null
    );
  }, [warehouseOptions, formData.warehouseId]);

  const localizedStatusOptions = useMemo(
    () => statusOptions.map((option) => ({ ...option, label: t(option.label) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang]
  );

  const selectedStatusOption = useMemo(() => {
    return (
      localizedStatusOptions.find((option) => option.value === formData.status) ||
      localizedStatusOptions[1]
    );
  }, [localizedStatusOptions, formData.status]);

  const selectedProductOption = useMemo(() => {
    const currentId = normalizeId(selectedProductId);
    return (
      productOptions.find(
        (option) => normalizeId(option.value) === currentId
      ) || null
    );
  }, [productOptions, selectedProductId]);

  const calculateItem = (item) => {
    const quantity = Math.max(1, Math.trunc(toNumber(item.quantity, 1)));
    const purchasePrice = Math.max(0, toNumber(item.purchasePrice, 0));
    const discountInput = Math.max(0, toNumber(item.discount, 0));
    const tax = Math.max(0, toNumber(item.tax, 0));

    const baseAmount = round2(quantity * purchasePrice);
    const discount = Math.min(discountInput, baseAmount);
    const taxableAmount = Math.max(0, baseAmount - discount);
    const taxAmount = round2((taxableAmount * tax) / 100);
    const totalCost = round2(taxableAmount + taxAmount);
    const unitCost = quantity > 0 ? round2(totalCost / quantity) : 0;

    return {
      ...item,
      quantity,
      purchasePrice,
      discount,
      tax,
      taxAmount,
      unitCost,
      totalCost,
    };
  };

  useEffect(() => {
    if (!purchaseData) {
      setFormData(buildEmptyForm());
      setItems([]);
      setSelectedProductId("");
      return;
    }

    const purchaseSupplierId = normalizeId(
      purchaseData?.supplier?._id || purchaseData?.supplier
    );
    const purchaseWarehouseId = normalizeId(
      purchaseData?.warehouse?._id || purchaseData?.warehouse
    );
    const purchaseStatus =
      statusOptions.find((item) => item.value === purchaseData?.status)?.value ||
      "Pending";

    setFormData({
      id: normalizeId(purchaseData?._id),
      supplierId: purchaseSupplierId,
      warehouseId: purchaseWarehouseId,
      purchaseDate: purchaseData?.purchaseDate
        ? dayjs(purchaseData.purchaseDate)
        : dayjs(),
      reference: purchaseData?.reference || "",
      orderTax: toNumber(purchaseData?.orderTax, 0),
      discount: toNumber(purchaseData?.discount, 0),
      shipping: toNumber(purchaseData?.shipping, 0),
      paid: toNumber(purchaseData?.paid, 0),
      status: purchaseStatus,
      note: purchaseData?.note || "",
    });

    const mappedItems = Array.isArray(purchaseData?.items)
      ? purchaseData.items.map((item) =>
          calculateItem({
            product: normalizeId(getProductId(item?.product) || item?.product),
            variant: normalizeId(item?.variant?._id || item?.variant) || null,
            warehouse:
              normalizeId(
                item?.warehouse?._id ||
                  item?.warehouse ||
                  purchaseData?.warehouse?._id ||
                  purchaseData?.warehouse
              ) || null,
            productName:
              item?.productName ||
              getProductDisplayName(item?.product, lang) ||
              t("Product"),
            sku: item?.sku || getProductSku(item?.product) || "",
            quantity: toNumber(item?.quantity, 1),
            purchasePrice: toNumber(item?.purchasePrice, 0),
            discount: toNumber(item?.discount, 0),
            tax: toNumber(item?.tax, 0),
            taxAmount: toNumber(item?.taxAmount, 0),
            unitCost: toNumber(item?.unitCost, 0),
            totalCost: toNumber(item?.totalCost, 0),
          })
        )
      : [];

    setItems(mappedItems);
    setSelectedProductId("");
  }, [purchaseData, lang]);

  useEffect(() => {
    if (!formData.warehouseId) return;

    setItems((prev) =>
      prev.map((item) => ({
        ...item,
        warehouse: item.warehouse || formData.warehouseId,
      }))
    );
  }, [formData.warehouseId]);

  const addProductItem = () => {
    if (!selectedProductId) {
      alert(t("Please select product"));
      return;
    }

    const selectedProduct = productOptions.find(
      (option) => normalizeId(option.value) === normalizeId(selectedProductId)
    );

    if (!selectedProduct?.raw) {
      alert(t("Selected product is invalid"));
      return;
    }

    const product = selectedProduct.raw;
    const productId = normalizeId(getProductId(product));

    if (!productId) {
      alert(t("Product id not found"));
      return;
    }

    const existingIndex = items.findIndex(
      (item) => normalizeId(item.product) === productId
    );

    if (existingIndex !== -1) {
      const updated = [...items];
      updated[existingIndex] = calculateItem({
        ...updated[existingIndex],
        quantity: Number(updated[existingIndex].quantity || 0) + 1,
      });
      setItems(updated);
      setSelectedProductId("");
      return;
    }

    const newItem = calculateItem({
      product: productId,
      variant: null,
      warehouse: formData.warehouseId || null,
      productName: getProductDisplayName(product, lang),
      sku: getProductSku(product),
      quantity: 1,
      purchasePrice: 0,
      discount: 0,
      tax: 0,
      taxAmount: 0,
      unitCost: 0,
      totalCost: 0,
    });

    setItems((prev) => [...prev, newItem]);
    setSelectedProductId("");
  };

  const updateItem = (index, field, value) => {
    setItems((prev) => {
      const updated = [...prev];
      const current = { ...updated[index] };

      if (field === "productName" || field === "sku") {
        current[field] = value;
      } else if (field === "quantity") {
        current[field] = Math.max(1, Math.trunc(toNumber(value, 1)));
      } else {
        current[field] = Math.max(0, toNumber(value, 0));
      }

      updated[index] = calculateItem(current);
      return updated;
    });
  };

  const removeItem = (index) => {
    setItems((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const subTotal = useMemo(() => {
    return round2(
      items.reduce((sum, item) => sum + toNumber(item.totalCost, 0), 0)
    );
  }, [items]);

  const grandTotal = useMemo(() => {
    const orderTax = Math.max(0, toNumber(formData.orderTax, 0));
    const shipping = Math.max(0, toNumber(formData.shipping, 0));
    const discount = Math.max(0, toNumber(formData.discount, 0));

    return round2(Math.max(0, subTotal + orderTax + shipping - discount));
  }, [subTotal, formData.orderTax, formData.shipping, formData.discount]);

  const resetForm = () => {
    setFormData(buildEmptyForm());
    setItems([]);
    setSelectedProductId("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.id) {
      alert(t("No purchase selected"));
      return;
    }

    if (!formData.supplierId) {
      alert(t("Please select supplier"));
      return;
    }

    if (!formData.reference.trim()) {
      alert(t("Please enter reference no"));
      return;
    }

    if (!items.length) {
      alert(t("Please add at least one product"));
      return;
    }

    try {
      setLoading(true);

      const payload = {
        supplier: formData.supplierId,
        warehouse: formData.warehouseId || null,
        purchaseDate: formData.purchaseDate
          ? dayjs(formData.purchaseDate).format("YYYY-MM-DD")
          : null,
        reference: formData.reference.trim(),
        items: items.map((item) => ({
          product: item.product,
          variant: item.variant || null,
          warehouse: item.warehouse || formData.warehouseId || null,
          productName: item.productName,
          sku: item.sku,
          quantity: Number(item.quantity || 0),
          purchasePrice: Number(item.purchasePrice || 0),
          discount: Number(item.discount || 0),
          tax: Number(item.tax || 0),
        })),
        orderTax: Number(formData.orderTax || 0),
        discount: Number(formData.discount || 0),
        shipping: Number(formData.shipping || 0),
        paid: Number(formData.paid || 0),
        status: formData.status || "Pending",
        note: formData.note || "",
      };

      await updatePurchase(formData.id, payload);

      if (typeof onSuccess === "function") {
        onSuccess();
      }

      const closeBtn = document.querySelector("#edit-units .close");
      if (closeBtn) closeBtn.click();
    } catch (error) {
      console.error(error);
      alert(error?.message || t("Failed to update purchase"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="modal fade" id="edit-units">
        <div className="modal-dialog purchase modal-dialog-centered stock-adjust-modal modal-xl">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header border-0 custom-modal-header">
                  <div className="page-title">
                    <h4>{t("Edit Purchase")}</h4>
                  </div>
                  <button
                    type="button"
                    className="close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                    onClick={resetForm}
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>

                <div className="modal-body custom-modal-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="input-blocks">
                          <label>{t("Supplier Name")}</label>
                          <Select
                            {...commonSelectProps}
                            instanceId="edit-purchase-supplier-select"
                            options={supplierOptions}
                            value={selectedSupplierOption}
                            onChange={(selected) =>
                              setFormData((prev) => ({
                                ...prev,
                                supplierId: normalizeId(selected?.value),
                              }))
                            }
                            placeholder={dropdownLoading ? t("Loading...") : t("Choose")}
                          />
                        </div>
                      </div>

                      <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="input-blocks">
                          <label>{t("Purchase Date")}</label>
                          <div className="input-groupicon calender-input">
                            <Calendar className="info-img" />
                            <DatePicker
                              value={formData.purchaseDate}
                              onChange={(date) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  purchaseDate: date || dayjs(),
                                }))
                              }
                              className="filterdatepicker"
                              placeholder={t("Choose Date")}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="input-blocks">
                          <label>{t("Warehouse")}</label>
                          <Select
                            {...commonSelectProps}
                            instanceId="edit-purchase-warehouse-select"
                            options={warehouseOptions}
                            value={selectedWarehouseOption}
                            onChange={(selected) =>
                              setFormData((prev) => ({
                                ...prev,
                                warehouseId: normalizeId(selected?.value),
                              }))
                            }
                            placeholder={dropdownLoading ? t("Loading...") : t("Choose")}
                          />
                        </div>
                      </div>

                      <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="input-blocks">
                          <label>{t("Reference No")}</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.reference}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                reference: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-lg-10">
                        <div className="input-blocks">
                          <label>{t("Product Name")}</label>
                          <Select
                            {...commonSelectProps}
                            instanceId="edit-purchase-product-select"
                            options={productOptions}
                            value={selectedProductOption}
                            onChange={(selected) =>
                              setSelectedProductId(normalizeId(selected?.value))
                            }
                            placeholder={
                              dropdownLoading ? t("Loading...") : t("Choose Product")
                            }
                          />
                        </div>
                      </div>

                      <div className="col-lg-2 d-flex align-items-end">
                        <button
                          type="button"
                          className="btn btn-primary w-100"
                          onClick={addProductItem}
                        >
                          {t("Add Product")}
                        </button>
                      </div>

                      <div className="col-lg-12">
                        <div className="modal-body-table">
                          <div className="table-responsive">
                            <table className="table datanew" style={{ minWidth: "1220px" }}>
                              <thead>
                                <tr>
                                  <th style={{ minWidth: "150px" }}>{t("Product")}</th>
                                  <th style={{ minWidth: "120px" }}>{t("Qty")}</th>
                                  <th style={{ minWidth: "165px" }}>
                                    {t("Purchase Price($)")}
                                  </th>
                                  <th style={{ minWidth: "145px" }}>
                                    {t("Discount($)")}
                                  </th>
                                  <th style={{ minWidth: "120px" }}>{t("Tax(%)")}</th>
                                  <th style={{ minWidth: "140px" }}>
                                    {t("Tax Amount($)")}
                                  </th>
                                  <th style={{ minWidth: "130px" }}>
                                    {t("Unit Cost($)")}
                                  </th>
                                  <th style={{ minWidth: "130px" }}>
                                    {t("Total Cost($)")}
                                  </th>
                                  <th style={{ minWidth: "120px" }}>{t("Action")}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {items.length > 0 ? (
                                  items.map((item, index) => (
                                    <tr key={`${item.product}-${index}`}>
                                      <td>{item.productName}</td>
                                      <td>
                                        <div className="product-quantity">
                                          <span
                                            className="quantity-btn"
                                            onClick={() =>
                                              updateItem(
                                                index,
                                                "quantity",
                                                Math.max(1, Number(item.quantity || 1) + 1)
                                              )
                                            }
                                          >
                                            + <PlusCircle />
                                          </span>
                                          <input
                                            type="text"
                                            className="quntity-input"
                                            value={item.quantity}
                                            onChange={(e) =>
                                              updateItem(index, "quantity", e.target.value)
                                            }
                                          />
                                          <span
                                            className="quantity-btn"
                                            onClick={() =>
                                              updateItem(
                                                index,
                                                "quantity",
                                                Math.max(1, Number(item.quantity || 1) - 1)
                                              )
                                            }
                                          >
                                            <MinusCircle />
                                          </span>
                                        </div>
                                      </td>
                                      <td>
                                        <input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          className="form-control"
                                          value={item.purchasePrice}
                                          onChange={(e) =>
                                            updateItem(index, "purchasePrice", e.target.value)
                                          }
                                        />
                                      </td>
                                      <td>
                                        <input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          className="form-control"
                                          value={item.discount}
                                          onChange={(e) =>
                                            updateItem(index, "discount", e.target.value)
                                          }
                                        />
                                      </td>
                                      <td>
                                        <input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          className="form-control"
                                          value={item.tax}
                                          onChange={(e) =>
                                            updateItem(index, "tax", e.target.value)
                                          }
                                        />
                                      </td>
                                      <td>{Number(item.taxAmount || 0).toFixed(2)}</td>
                                      <td>{Number(item.unitCost || 0).toFixed(2)}</td>
                                      <td>{Number(item.totalCost || 0).toFixed(2)}</td>
                                      <td>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-danger"
                                          onClick={() => removeItem(index)}
                                        >
                                          {t("Remove")}
                                        </button>
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan="9" className="text-center p-4">
                                      {t("No products added")}
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-lg-3 col-md-6 col-sm-12">
                          <div className="input-blocks">
                            <label>{t("Order Tax")}</label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="form-control"
                              value={formData.orderTax}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  orderTax: e.target.value,
                                }))
                              }
                            />
                          </div>
                        </div>

                        <div className="col-lg-3 col-md-6 col-sm-12">
                          <div className="input-blocks">
                            <label>{t("Discount")}</label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="form-control"
                              value={formData.discount}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  discount: e.target.value,
                                }))
                              }
                            />
                          </div>
                        </div>

                        <div className="col-lg-3 col-md-6 col-sm-12">
                          <div className="input-blocks">
                            <label>{t("Shipping")}</label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="form-control"
                              value={formData.shipping}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  shipping: e.target.value,
                                }))
                              }
                            />
                          </div>
                        </div>

                        <div className="col-lg-3 col-md-6 col-sm-12">
                          <div className="input-blocks">
                            <label>{t("Status")}</label>
                            <Select
                              {...commonSelectProps}
                              instanceId="edit-purchase-status-select"
                              options={localizedStatusOptions}
                              value={selectedStatusOption}
                              onChange={(selected) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  status: selected?.value || "Pending",
                                }))
                              }
                              placeholder={t("Choose")}
                              isClearable={false}
                            />
                          </div>
                        </div>

                        <div className="col-lg-3 col-md-6 col-sm-12">
                          <div className="input-blocks">
                            <label>{t("Paid")}</label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="form-control"
                              value={formData.paid}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  paid: e.target.value,
                                }))
                              }
                            />
                          </div>
                        </div>

                        <div className="col-lg-3 col-md-6 col-sm-12">
                          <div className="input-blocks">
                            <label>{t("Sub Total")}</label>
                            <input
                              type="text"
                              className="form-control"
                              value={subTotal.toFixed(2)}
                              readOnly
                            />
                          </div>
                        </div>

                        <div className="col-lg-3 col-md-6 col-sm-12">
                          <div className="input-blocks">
                            <label>{t("Grand Total")}</label>
                            <input
                              type="text"
                              className="form-control"
                              value={grandTotal.toFixed(2)}
                              readOnly
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-12">
                      <div className="input-blocks summer-description-box">
                        <label>{t("Notes")}</label>
                        <textarea
                          className="form-control"
                          rows="4"
                          value={formData.note}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, note: e.target.value }))
                          }
                        />
                        <div className="d-none">
                          <TextEditor />
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-12">
                      <div className="modal-footer-btn">
                        <button
                          type="button"
                          className="btn btn-cancel me-2"
                          data-bs-dismiss="modal"
                          onClick={resetForm}
                        >
                          {window.translate("cancel")}
                        </button>
                        <button type="submit" className="btn btn-submit" disabled={loading}>
                          {loading ? t("Updating...") : window.translate("submit")}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

EditPurchases.propTypes = {
  purchaseData: PropTypes.shape({
    _id: PropTypes.string,
    supplier: objectIdOrObject,
    warehouse: objectIdOrObject,
    purchaseDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    reference: PropTypes.string,
    orderTax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    discount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    shipping: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    paid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string,
    note: PropTypes.string,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        product: productPropType,
        variant: objectIdOrObject,
        warehouse: objectIdOrObject,
        productName: PropTypes.string,
        sku: PropTypes.string,
        quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        purchasePrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        discount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        tax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        taxAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        unitCost: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        totalCost: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })
    ),
  }),
  onSuccess: PropTypes.func,
};

export default EditPurchases;
